import React, { useEffect, useState, useCallback } from 'react';
import { Users, Search, Send, CheckCircle2, Gauge, RefreshCw, Layers, Plus, Trash2, X, SlidersHorizontal } from 'lucide-react';
import { Link } from 'react-router-dom';
import api from '../../services/api';

interface Contact {
  id: string; name: string | null; email: string | null; phone: string | null;
  form_data: Record<string, any> | null; score: number | null; qualified: boolean;
  crm_pushed: boolean; crm_lead_id: string | null; source: string | null; utm_source: string | null; created_at: string;
}
interface Summary { total: number; qualified: number; weak: number; pushed: number; }
interface Segment { id: string; name: string; description?: string; conditions: any; is_shared: boolean; count: number; }

const FILTERS = [
  { key: 'all', label: 'Todos' },
  { key: 'qualified', label: 'Qualificados' },
  { key: 'weak', label: 'Baixa qualificação' },
  { key: 'pushed', label: 'No CRM' },
];
const STATUS_OPTS = [
  { v: 'all', l: 'Qualquer' }, { v: 'qualified', l: 'Qualificado' }, { v: 'weak', l: 'Baixa qualificação' },
  { v: 'pushed', l: 'Já no CRM' }, { v: 'notpushed', l: 'Fora do CRM' },
];

const ContactsPage: React.FC = () => {
  const [rows, setRows] = useState<Contact[]>([]);
  const [summary, setSummary] = useState<Summary>({ total: 0, qualified: 0, weak: 0, pushed: 0 });
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState<string | null>(null);
  // segmentações
  const [segments, setSegments] = useState<Segment[]>([]);
  const [activeSeg, setActiveSeg] = useState<Segment | null>(null);
  const [showSegs, setShowSegs] = useState(false);
  const [newSeg, setNewSeg] = useState<{ open: boolean; name: string; status: string; interest: string; event: string; dateFrom: string; dateTo: string; shared: boolean }>({ open: false, name: '', status: 'all', interest: '', event: '', dateFrom: '', dateTo: '', shared: false });
  const EVENTS = [
    { v: '', l: 'Qualquer origem' },
    { v: 'Formulário', l: 'Formulário de contato' },
    { v: 'Botão WhatsApp', l: 'Botão / pop-up WhatsApp' },
    { v: 'por e-mail', l: 'Card de e-mail' },
    { v: 'ligação', l: 'Card de ligação' },
  ];
  // lead score editável
  const [scoreOpen, setScoreOpen] = useState(false);
  const [scoreRules, setScoreRules] = useState<Array<{ field: string; operator: string; value: string; points: number }>>([]);
  const [threshold, setThreshold] = useState(30);
  const [scoreSaving, setScoreSaving] = useState(false);
  const OPERATORS = [
    { v: 'is_not_empty', l: 'está preenchido' }, { v: 'is_empty', l: 'está vazio' },
    { v: 'contains', l: 'contém' }, { v: 'not_contains', l: 'não contém' },
    { v: 'equals', l: 'é igual a' }, { v: 'greater_than', l: 'maior que' }, { v: 'less_than', l: 'menor que' },
  ];
  // envio pro CRM com tag
  const [pushModal, setPushModal] = useState<{ id: string; name: string } | null>(null);
  const [crmTags, setCrmTags] = useState<Array<{ id: string; name: string; color?: string }>>([]);
  const [selTags, setSelTags] = useState<string[]>([]);
  const [newCrmTag, setNewCrmTag] = useState('');
  const [tagSearch2, setTagSearch2] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = { search: search || undefined };
      if (activeSeg) params.segmentId = activeSeg.id; else params.status = status;
      const { data } = await api.get('/contacts', { params });
      setRows(data.data || []); setSummary(data.summary || { total: 0, qualified: 0, weak: 0, pushed: 0 });
    } catch (e) { console.error('[Contacts] load error', e); } finally { setLoading(false); }
  }, [status, search, activeSeg]);

  const loadSegments = useCallback(async () => {
    try { const { data } = await api.get('/contacts/segments'); setSegments(data.data || []); } catch (e) { /* silencioso */ }
  }, []);

  useEffect(() => { const t = setTimeout(load, search ? 350 : 0); return () => clearTimeout(t); }, [load, search]);
  useEffect(() => { loadSegments(); }, [loadSegments]);

  const applySeg = (s: Segment | null) => { setActiveSeg(s); if (s) setStatus('all'); setShowSegs(false); };
  const pickFilter = (k: string) => { setStatus(k); setActiveSeg(null); };

  const saveSeg = async () => {
    if (!newSeg.name.trim()) { alert('Dê um nome à segmentação'); return; }
    const conditions: any = {};
    if (newSeg.status !== 'all') conditions.status = newSeg.status;
    if (newSeg.interest.trim()) conditions.interest = newSeg.interest.trim();
    if (newSeg.event) conditions.event = newSeg.event;
    if (newSeg.dateFrom) conditions.dateFrom = newSeg.dateFrom;
    if (newSeg.dateTo) conditions.dateTo = newSeg.dateTo;
    try {
      await api.post('/contacts/segments', { name: newSeg.name.trim(), conditions, is_shared: newSeg.shared });
      setNewSeg({ open: false, name: '', status: 'all', interest: '', event: '', dateFrom: '', dateTo: '', shared: false });
      loadSegments();
    } catch (e: any) { alert(e?.response?.data?.message || 'Erro ao salvar'); }
  };
  const delSeg = async (id: string) => {
    if (!window.confirm('Apagar esta segmentação?')) return;
    try { await api.delete(`/contacts/segments/${id}`); if (activeSeg?.id === id) setActiveSeg(null); loadSegments(); }
    catch (e: any) { alert(e?.response?.data?.message || 'Erro ao apagar'); }
  };

  const openPush = async (id: string, name: string) => {
    setPushModal({ id, name }); setSelTags([]); setTagSearch2(''); setNewCrmTag('');
    try { const { data } = await api.get('/contacts/crm-tags'); setCrmTags(data.data || []); } catch (e) { setCrmTags([]); }
  };
  const toggleTag = (name: string) => setSelTags((s) => (s.includes(name) ? s.filter((x) => x !== name) : s.length >= 2 ? s : [...s, name]));
  const createCrmTag = async () => {
    const nm = newCrmTag.trim(); if (!nm) return;
    try { const { data } = await api.post('/contacts/crm-tags', { name: nm }); setCrmTags((t) => [data.data, ...t]); setSelTags((s) => (s.length < 2 ? [...s, data.data.name] : s)); setNewCrmTag(''); }
    catch (e: any) { alert(e?.response?.data?.message || 'Erro ao criar a tag'); }
  };
  const doPush = async () => {
    if (!pushModal) return; const id = pushModal.id; setPushing(id);
    try {
      const { data } = await api.post(`/contacts/${id}/push-to-crm`, { tags: selTags });
      if (data.success) { setRows((r) => r.map((c) => (c.id === id ? { ...c, crm_pushed: true, crm_lead_id: data.crmLeadId } : c))); setSummary((s) => ({ ...s, pushed: s.pushed + 1 })); setPushModal(null); }
      else alert(data.message || 'Falha ao enviar ao CRM');
    } catch (e: any) { alert(e?.response?.data?.message || 'Erro ao enviar ao CRM'); } finally { setPushing(null); }
  };

  const openScore = async () => {
    setScoreOpen(true);
    try { const { data } = await api.get('/contacts/score-config'); setScoreRules(data.data.rules || []); setThreshold(data.data.threshold ?? 30); } catch (e) { /* silencioso */ }
  };
  const saveScore = async () => {
    setScoreSaving(true);
    try { await api.put('/contacts/score-config', { rules: scoreRules, threshold }); setScoreOpen(false); loadSegments(); }
    catch (e: any) { alert(e?.response?.data?.message || 'Erro ao salvar a pontuação'); }
    finally { setScoreSaving(false); }
  };
  const setRule = (i: number, patch: Partial<{ field: string; operator: string; value: string; points: number }>) =>
    setScoreRules((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const scoreBadge = (score: number | null, qualified: boolean) => {
    const cls = qualified
      ? 'bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-200 dark:border-emerald-500/30'
      : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-600';
    return <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-xs font-mono ${cls}`}><Gauge className="w-3 h-3" />{score ?? 0}</span>;
  };

  const crmCell = (c: Contact) => c.crm_pushed
    ? <span className="inline-flex items-center gap-1 text-blue-600 dark:text-blue-400 text-xs"><CheckCircle2 className="w-4 h-4" />No CRM</span>
    : <span className="text-gray-500 dark:text-gray-400 text-xs">Só no Marketing</span>;
  const pushBtn = (c: Contact, full = false) => c.crm_pushed ? null : (
    <button onClick={() => openPush(c.id, c.name || c.email || '')} disabled={pushing === c.id}
      className={`inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-xs font-semibold whitespace-nowrap disabled:opacity-50 ${full ? 'w-full' : ''}`}>
      <Send className="w-3.5 h-3.5" />{pushing === c.id ? 'Enviando…' : 'Enviar pro CRM'}
    </button>
  );

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Cabeçalho no padrão das outras telas: título + ações numa linha */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Contatos</h1>
          <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">Quem deixou nome e telefone nas suas páginas. Os mais interessados vão para o CRM; os outros ficam aqui.</p>
        </div>
        {/* Ações da base — separadas dos filtros de status de propósito: são
            ferramentas (segmentar / configurar pontuação), não recortes da lista. */}
        <div className="flex items-center gap-2 shrink-0">
          <button onClick={openScore} aria-label="Pontuação"
            className="h-10 px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 text-sm font-medium inline-flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4" /><span className="hidden sm:inline">Pontuação</span>
          </button>
          <button onClick={() => setShowSegs(true)}
            className="h-10 px-4 rounded-lg bg-gradient-to-r from-primary-600 to-primary-700 text-white text-sm font-semibold inline-flex items-center gap-2 shadow-sm whitespace-nowrap">
            <Layers className="w-4 h-4" />Segmentações
          </button>
        </div>
      </div>

      {/* Números */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
        {[
          { label: 'Total de contatos', val: summary.total, box: 'border-blue-200 dark:border-blue-800 from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20', tx: 'text-blue-600 dark:text-blue-400', num: 'text-blue-700 dark:text-blue-300' },
          { label: 'Qualificados', val: summary.qualified, box: 'border-green-200 dark:border-green-800 from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20', tx: 'text-green-600 dark:text-green-400', num: 'text-green-700 dark:text-green-300' },
          { label: 'Baixa qualificação', val: summary.weak, box: 'border-orange-200 dark:border-orange-800 from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20', tx: 'text-orange-600 dark:text-orange-400', num: 'text-orange-700 dark:text-orange-300' },
          { label: 'Enviados ao CRM', val: summary.pushed, box: 'border-purple-200 dark:border-purple-800 from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20', tx: 'text-purple-600 dark:text-purple-400', num: 'text-purple-700 dark:text-purple-300' },
        ].map((c) => (
          <div key={c.label} className={`rounded-xl border bg-gradient-to-br p-3 sm:p-5 ${c.box}`}>
            <p className={`text-xs sm:text-sm font-medium truncate ${c.tx}`}>{c.label}</p>
            <p className={`text-lg sm:text-2xl font-bold ${c.num}`}>{c.val}</p>
          </div>
        ))}
      </div>

      {/* Filtros numa caixa só */}
      <div className="card p-3 sm:p-4 space-y-3">
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f.key} onClick={() => pickFilter(f.key)}
              className={`px-3 py-1.5 rounded-full border text-sm font-medium transition ${!activeSeg && status === f.key ? 'bg-primary-600 border-primary-600 text-white' : 'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700'}`}>{f.label}</button>
          ))}
        </div>
        <div className="flex gap-2">
          <div className="relative flex-1 min-w-0">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar nome, e-mail, telefone"
              className="w-full pl-9 pr-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500" />
          </div>
          <button onClick={load} className="px-3 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700" title="Atualizar" aria-label="Atualizar"><RefreshCw className="w-4 h-4" /></button>
        </div>
        {activeSeg && (
          <div className="flex items-center gap-2 text-sm">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 font-medium min-w-0">
              <Layers className="w-3.5 h-3.5 shrink-0" /><span className="truncate">{activeSeg.name}</span> <span className="opacity-70">({rows.length})</span>
            </span>
            <button onClick={() => setActiveSeg(null)} className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-xs underline">limpar</button>
          </div>
        )}
      </div>

      {/* Lista */}
      <div className="card p-0 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-gray-500 dark:text-gray-400">Carregando…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center">
            <div className="w-14 h-14 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-3"><Users className="w-7 h-7 text-gray-400" /></div>
            {search || status !== FILTERS[0].key || activeSeg ? (
              <p className="text-gray-500 dark:text-gray-400">Nenhum contato com esse filtro.</p>
            ) : (
              <>
                <p className="font-medium text-gray-900 dark:text-white">Ainda não chegou nenhum contato</p>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 max-w-sm mx-auto">Eles aparecem aqui quando alguém preenche o formulário de uma página de venda.</p>
                <Link to="/landing-pages" className="mt-4 inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold">
                  Ir para Páginas de venda
                </Link>
              </>
            )}
          </div>
        ) : (
          <>
            {/* Celular: cartões */}
            <ul className="sm:hidden divide-y divide-gray-200 dark:divide-gray-700">
              {rows.map((c) => (
                <li key={c.id} className="p-3 space-y-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900 dark:text-white truncate">{c.name || '—'}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{c.email || c.phone || '—'}</p>
                    </div>
                    {scoreBadge(c.score, c.qualified)}
                  </div>
                  {c.form_data?.produto_interesse && <p className="text-xs text-gray-600 dark:text-gray-300 break-words">Interesse: {c.form_data.produto_interesse}</p>}
                  {c.crm_pushed ? crmCell(c) : pushBtn(c, true)}
                </li>
              ))}
            </ul>
            {/* Computador: tabela */}
            <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 dark:bg-gray-800/50 text-gray-600 dark:text-gray-400 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Contato</th>
                    <th className="text-left px-4 py-3 font-semibold">Interesse</th>
                    <th className="text-left px-4 py-3 font-semibold">Score</th>
                    <th className="text-left px-4 py-3 font-semibold">CRM</th>
                    <th className="text-right px-4 py-3 font-semibold">Ação</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {rows.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50">
                      <td className="px-4 py-3 max-w-[280px]"><div className="text-gray-900 dark:text-white font-medium truncate">{c.name || '—'}</div><div className="text-gray-500 dark:text-gray-400 text-xs truncate">{c.email || c.phone || '—'}</div></td>
                      <td className="px-4 py-3 text-gray-700 dark:text-gray-300 max-w-[260px] truncate">{c.form_data?.produto_interesse || '—'}</td>
                      <td className="px-4 py-3">{scoreBadge(c.score, c.qualified)}</td>
                      <td className="px-4 py-3">{crmCell(c)}</td>
                      <td className="px-4 py-3 text-right">{c.crm_pushed ? <span className="text-gray-400 dark:text-gray-500 text-xs">—</span> : pushBtn(c)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Segmentações: folha de baixo no celular, janela no computador */}
      {showSegs && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-start sm:justify-center sm:p-6 sm:pt-24" onClick={() => setShowSegs(false)}>
          <div className="w-full sm:max-w-md max-h-[85vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl p-4 pb-[max(1rem,env(safe-area-inset-bottom))]" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <span className="text-base font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Layers className="w-5 h-5" /> Segmentações</span>
              <button onClick={() => setShowSegs(false)} className="p-1 text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white" aria-label="Fechar"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-1">
              {segments.length === 0 && <p className="text-sm text-gray-500 dark:text-gray-400 py-2">Nenhuma segmentação ainda.</p>}
              {segments.map((s) => (
                <div key={s.id} className="flex items-center gap-2">
                  <button onClick={() => applySeg(s)} className={`flex-1 min-w-0 text-left px-3 py-2.5 rounded-lg text-sm ${activeSeg?.id === s.id ? 'bg-purple-100 dark:bg-purple-600/30 text-purple-800 dark:text-white' : 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300'}`}>
                    <span className="font-medium">{s.name}</span>
                    <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">{s.count}</span>
                    {s.is_shared && <span className="ml-2 text-[10px] uppercase tracking-wide text-purple-500">compartilhada</span>}
                  </button>
                  <button onClick={() => delSeg(s.id)} className="text-gray-400 dark:text-gray-500 hover:text-red-500 p-2" title="Apagar" aria-label="Apagar"><Trash2 className="w-4 h-4" /></button>
                </div>
              ))}
            </div>
            <div className="border-t border-gray-200 dark:border-gray-700 mt-3 pt-3">
              {!newSeg.open ? (
                <button onClick={() => setNewSeg((n) => ({ ...n, open: true }))} className="inline-flex items-center gap-1.5 text-sm font-medium text-primary-600 hover:text-primary-500"><Plus className="w-4 h-4" /> Nova segmentação</button>
              ) : (
                <div className="space-y-2">
                  <input value={newSeg.name} onChange={(e) => setNewSeg((n) => ({ ...n, name: e.target.value }))} placeholder="Nome da segmentação"
                    className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white" />
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <select value={newSeg.status} onChange={(e) => setNewSeg((n) => ({ ...n, status: e.target.value }))}
                      className="w-full px-2 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white">
                      {STATUS_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                    </select>
                    <input value={newSeg.interest} onChange={(e) => setNewSeg((n) => ({ ...n, interest: e.target.value }))} placeholder="Interesse contém…"
                      className="w-full px-3 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white" />
                  </div>
                  <select value={newSeg.event} onChange={(e) => setNewSeg((n) => ({ ...n, event: e.target.value }))}
                    className="w-full px-2 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white">
                    {EVENTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                  </select>
                  <div>
                    <span className="text-xs text-gray-500 dark:text-gray-400">Período</span>
                    <div className="flex gap-2 mt-1">
                      <input type="date" value={newSeg.dateFrom} onChange={(e) => setNewSeg((n) => ({ ...n, dateFrom: e.target.value }))} className="flex-1 min-w-0 px-2 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white" />
                      <input type="date" value={newSeg.dateTo} onChange={(e) => setNewSeg((n) => ({ ...n, dateTo: e.target.value }))} className="flex-1 min-w-0 px-2 py-2 rounded-lg bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 text-sm text-gray-900 dark:text-white" />
                    </div>
                  </div>
                  <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400"><input type="checkbox" checked={newSeg.shared} onChange={(e) => setNewSeg((n) => ({ ...n, shared: e.target.checked }))} /> Compartilhar com toda a conta</label>
                  <div className="flex gap-2">
                    <button onClick={() => setNewSeg({ open: false, name: '', status: 'all', interest: '', event: '', dateFrom: '', dateTo: '', shared: false })} className="flex-1 sm:flex-none px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 text-sm">Cancelar</button>
                    <button onClick={saveSeg} className="flex-1 sm:flex-none px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm font-semibold">Salvar</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Editor do lead score */}
      {scoreOpen && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto p-3 sm:p-6" onClick={() => setScoreOpen(false)}>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-3xl mt-4 sm:mt-8" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-gray-900 dark:text-white font-semibold flex items-center gap-2"><SlidersHorizontal className="w-5 h-5" /> Pontuação (lead score)</h3>
              <button onClick={() => setScoreOpen(false)} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-4 sm:p-5 space-y-4">
              <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                <span className="text-sm text-gray-600 dark:text-gray-400">Nota de corte (vai pro CRM a partir de):</span>
                <input type="number" value={threshold} onChange={(e) => setThreshold(Number(e.target.value))}
                  className="w-24 px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm" />
              </div>
              <div className="space-y-2">
                <div className="hidden sm:grid grid-cols-12 gap-2 text-[11px] uppercase text-gray-500 dark:text-gray-400 px-1">
                  <span className="col-span-4">Campo</span><span className="col-span-3">Condição</span><span className="col-span-3">Valor</span><span className="col-span-1">Pts</span><span className="col-span-1"></span>
                </div>
                {scoreRules.map((r, i) => {
                  const noVal = r.operator === 'is_empty' || r.operator === 'is_not_empty';
                  return (
                    <div key={i} className="grid grid-cols-6 sm:grid-cols-12 gap-2 items-center p-2 sm:p-0 rounded-lg border sm:border-0 border-gray-200 dark:border-gray-700">
                      <input value={r.field} onChange={(e) => setRule(i, { field: e.target.value })} placeholder="form.produto_interesse"
                        className="col-span-6 sm:col-span-4 px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm" />
                      <select value={r.operator} onChange={(e) => setRule(i, { operator: e.target.value })}
                        className="col-span-3 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm">
                        {OPERATORS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                      <input value={noVal ? '' : r.value} disabled={noVal} onChange={(e) => setRule(i, { value: e.target.value })} placeholder={noVal ? '—' : 'valor'}
                        className="col-span-3 px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm disabled:opacity-40" />
                      <input type="number" value={r.points} onChange={(e) => setRule(i, { points: Number(e.target.value) })}
                        className="col-span-4 sm:col-span-1 px-2 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white text-sm" />
                      <button onClick={() => setScoreRules((rs) => rs.filter((_, idx) => idx !== i))} className="col-span-2 sm:col-span-1 text-gray-400 dark:text-gray-500 hover:text-red-400 flex justify-center" aria-label="Apagar regra"><Trash2 className="w-4 h-4" /></button>
                    </div>
                  );
                })}
                <button onClick={() => setScoreRules((rs) => [...rs, { field: 'form.produto_interesse', operator: 'contains', value: '', points: 10 }])}
                  className="inline-flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 mt-1"><Plus className="w-4 h-4" /> Adicionar regra</button>
              </div>
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button onClick={() => setScoreOpen(false)} className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm">Cancelar</button>
                <button onClick={saveScore} disabled={scoreSaving} className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm disabled:opacity-50">{scoreSaving ? 'Salvando…' : 'Salvar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enviar pro CRM: escolher tag(s) do CRM + score junto */}
      {pushModal && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center p-3 sm:p-6" onClick={() => setPushModal(null)}>
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-2xl w-full max-w-md mt-16" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-gray-900 dark:text-white font-semibold flex items-center gap-2"><Send className="w-5 h-5" /> Enviar pro CRM</h3>
              <button onClick={() => setPushModal(null)} className="text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-gray-600 dark:text-gray-400">Contato: <span className="text-gray-900 dark:text-white">{pushModal.name || '—'}</span></p>
              <p className="text-xs text-gray-500 dark:text-gray-400">Escolha até 2 etiquetas (opcional). A pontuação vai junto.</p>
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 dark:text-gray-400" />
                <input value={tagSearch2} onChange={(e) => setTagSearch2(e.target.value)} placeholder="Buscar etiqueta do CRM…" className="w-full pl-9 pr-3 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white" />
              </div>
              <div className="flex flex-wrap gap-1.5 max-h-40 overflow-y-auto">
                {crmTags.filter((t) => t.name.toLowerCase().includes(tagSearch2.toLowerCase())).map((t) => (
                  <button key={t.id} onClick={() => toggleTag(t.name)} disabled={!selTags.includes(t.name) && selTags.length >= 2}
                    className={`px-2.5 py-1 rounded-full text-xs border ${selTags.includes(t.name) ? 'bg-primary-600 border-primary-500 text-white' : 'bg-gray-100 dark:bg-gray-700 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white'} disabled:opacity-40`}>
                    {t.name}
                  </button>
                ))}
                {crmTags.length === 0 && <span className="text-xs text-gray-400 dark:text-gray-500">Nenhuma tag no CRM ainda.</span>}
              </div>
              <div className="flex gap-2">
                <input value={newCrmTag} onChange={(e) => setNewCrmTag(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && createCrmTag()} placeholder="Criar nova etiqueta…" className="flex-1 px-2.5 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white" />
                <button onClick={createCrmTag} className="px-3 py-1.5 rounded-lg bg-gray-100 dark:bg-gray-700 text-primary-400 text-sm inline-flex items-center gap-1"><Plus className="w-4 h-4" />Criar</button>
              </div>
              {selTags.length > 0 && <p className="text-xs text-gray-500 dark:text-gray-400">Selecionadas: <span className="text-primary-400">{selTags.join(', ')}</span></p>}
              <div className="flex justify-end gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button onClick={() => setPushModal(null)} className="px-4 py-2 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-sm">Cancelar</button>
                <button onClick={doPush} disabled={pushing === pushModal.id} className="px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-500 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"><Send className="w-4 h-4" />{pushing === pushModal.id ? 'Enviando…' : 'Enviar'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
